import React, { useEffect, useState } from "react";
import { Box, List, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getInputProps,
  getInputLabelProps,
  PopperFullWidth,
} from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import { TabContext, TabPanel } from "@mui/lab";
import ScrollablePageTab from "../../dynamicForm/components/ScrollablePageTab";
import NotificationItem from "../../../components/common/NotificationItem";
import VehicleMovementItem from "./VehicleMovementItem";
import ExpandableView from "../../../components/common/ExpandableView";

export default function AddVehicle() {
  const {
    vehicleModel,
    setVehicleModel,
    config,
    VEHICLE_ENDPOINT,
    PERSONAL_API,
    VEHICLE_TYPE_ENDPOINT,
  } = useGlobalContext();
  const [vehicleAssignmentHistoryState, setVehicleAssignmentHistoryState] =
    useState();
  const [vehicleTypeData, setVehicleTypeData] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [selectedTab, setSelectedTab] = useState("0");

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  const getVehicleTypes = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${VEHICLE_TYPE_ENDPOINT}/`, requestConfig);

    setVehicleTypeData(data?.data);
  };

  useEffect(() => {
    getPersonal();
    getVehicleTypes();
  }, []);

  const getVehicleAssignmentHistory = async () => {
    const data = await axios.get(
      `${VEHICLE_ENDPOINT}/${vehicleModel.Id}/assignment`,
      config
    );
    setVehicleAssignmentHistoryState(data.data ?? []);
  };

  const handleChange = (e) => {
    setVehicleModel({
      ...vehicleModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleVehicleTypeChange = (selectedVehicleType) => {
    if (selectedVehicleType) {
      setVehicleModel({
        ...vehicleModel,
        VehicleType: selectedVehicleType,
        VehicleTypeId: selectedVehicleType?.Id ?? 0,
      });
    } else {
      setVehicleModel({
        ...vehicleModel,
        VehicleType: null,
        VehicleTypeId: 0,
      });
    }
  };

  const handlePersonalChange = (selectedPersonal) => {
    if (selectedPersonal) {
      setVehicleModel({
        ...vehicleModel,
        Personal: selectedPersonal,
        PersonalId: selectedPersonal?.id ?? 0,
      });
    } else {
      setVehicleModel({
        ...vehicleModel,
        Personal: null,
        PersonalId: 0,
      });
    }
  };

  const handleSoatChange = (selectedSoatDate) => {
    if (selectedSoatDate) {
      setVehicleModel({
        ...vehicleModel,
        Soat: selectedSoatDate,
      });
    }
  };

  const handleTechnicalReviewChange = (selectedSoatDate) => {
    if (selectedSoatDate) {
      setVehicleModel({
        ...vehicleModel,
        TechnicalReview: selectedSoatDate,
      });
    }
  };

  useEffect(() => {
    if (vehicleModel?.Id) {
      getVehicleAssignmentHistory();
    }
  }, [vehicleModel?.Id]);

  return (
    <div style={{ width: "100%" }}>
      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <form className="w-full ">
                <div className="w-full my-6">
                  <Combobox
                    isRequired
                    id="VehicleTypeId"
                    handleValueChange={handleVehicleTypeChange}
                    fieldLabel={localize.vehicle_type}
                    fieldOptions={vehicleTypeData?.Data ?? []}
                    fieldValue={vehicleModel?.VehicleType ?? null}
                  />
                </div>

                <div className="flex justify-between my-4">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    name="Identification"
                    label={localize.identification}
                    variant="outlined"
                    onChange={handleChange}
                    value={vehicleModel?.Identification}
                  />
                  <div className="mx-2" />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={localize.soat}
                      className="w-1/2"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      PopperComponent={PopperFullWidth}
                      value={vehicleModel?.Soat}
                      onChange={handleSoatChange}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={localize.technomechanic_review}
                    className="w-full"
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    PopperComponent={PopperFullWidth}
                    value={vehicleModel?.TechnicalReview}
                    onChange={handleTechnicalReviewChange}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                <div className="w-full my-4">
                  <Combobox
                    id="Personal"
                    popperFullWidth={PopperFullWidth}
                    handleValueChange={handlePersonalChange}
                    fieldLabel={localize.assigned_to}
                    fieldOptions={personalData?.Data ?? []}
                    fieldValue={vehicleModel?.Personal ?? null}
                    customOptionLabel={(option) =>
                      `${option.Identification} - ${option.SurName} ${option.LastName}`
                    }
                  />
                </div>
              </form>
            ),
          },
          {
            Name: localize.assignment_history,
            Component: (
              <div className="w-full my-2">
                <List className="w-full flex flex-col gap-2">
                  {vehicleAssignmentHistoryState?.Data?.length > 0 ? (
                    vehicleAssignmentHistoryState?.Data?.map((assignment) => (
                      <VehicleMovementItem assignment={assignment} />
                    ))
                  ) : (
                    <NotificationItem
                      notification={{
                        type: 0,
                        message: localize.no_data,
                      }}
                    />
                  )}
                </List>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
